import type * as React from "react"
import {
  BarChart2,
  CalendarClock,
  CalendarDays,
  Clapperboard,
  Film,
  Globe,
  Image,
  Inbox,
  Key,
  ListTree,
  Mail,
  MessageCircleQuestionMark,
  MessageSquareQuote,
  Palette,
  PhoneCall,
  Radar,
  Search,
  Sparkles,
  ToggleLeft,
  UserCircle,
  Video,
} from "lucide-react"
import type { AdminCounts } from "@/types/inertia"

// The admin portal's menu, shared by the sidebar (AdminShell) and the admin
// bar's "Sections" dropdown on public pages. Counts are optional: the bar has
// no admin_counts prop, so its items simply carry no badge.

// The one dark surface in the app — the admin sidebar, its mobile header and
// the admin bar all paint it. Not a design-system token (there is no inverted
// surface in the palette), so it lives here as the single source.
export const ADMIN_BG = "#0F2B30"

export type NavItem = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  match: (url: string) => boolean
  count?: number
}

export type NavSection = { label: string; items: NavItem[] }

// Brand-first menu (docs/two-brand-split-strategy.md §13.3): each website has
// its own section; cross-brand tools and system items stay separate. Matching
// is query-aware because both brands share controller URLs (?brand=).
function matchWithBrand(url: string, path: string, brand: string) {
  return url.startsWith(path) && url.includes(`brand=${brand}`)
}

function overviewItems(): NavItem[] {
  return [
    {
      href: "/admin/analytics",
      label: "Dashboard",
      icon: BarChart2,
      match: (url) =>
        (url.startsWith("/admin/analytics") || url === "/admin") && !url.includes("brand="),
    },
  ]
}

function giselaItems(counts?: AdminCounts): NavItem[] {
  return [
    {
      href: "/admin/analytics?brand=gisela",
      label: "Analytics",
      icon: BarChart2,
      match: (url) => matchWithBrand(url, "/admin/analytics", "gisela"),
    },
    {
      href: "/admin/enquiries?brand=gisela",
      label: "Enquiries",
      icon: Inbox,
      count: counts?.therapy_enquiries,
      match: (url) => matchWithBrand(url, "/admin/enquiries", "gisela"),
    },
    {
      href: "/admin/consultations",
      label: "Consultations",
      icon: CalendarClock,
      count: counts?.consultation_bookings_upcoming,
      match: (url) => url.startsWith("/admin/consultations"),
    },
    {
      href: "/admin/callback-requests",
      label: "Callback Requests",
      icon: PhoneCall,
      count: counts?.callback_requests_pending,
      match: (url) => url.startsWith("/admin/callback-requests"),
    },
    {
      href: "/admin/availability",
      label: "Availability",
      icon: CalendarDays,
      match: (url) => url.startsWith("/admin/availability"),
    },
    {
      href: "/admin/website?brand=gisela",
      label: "Website Pages",
      icon: ListTree,
      match: (url) =>
        matchWithBrand(url, "/admin/website", "gisela") ||
        (url.startsWith("/admin/website/pages/") &&
          ["ocd-therapy", "online-therapy", "gisela-"].some((k) => url.includes(k))),
    },
    {
      href: "/admin/articles",
      label: "Blog",
      icon: Globe,
      count: counts?.articles,
      match: (url) => url.startsWith("/admin/articles"),
    },
    {
      href: "/admin/studio",
      label: "Content Studio",
      icon: Clapperboard,
      count: counts?.studio_pending,
      match: (url) => url.startsWith("/admin/studio"),
    },
    {
      href: "/admin/newsletter",
      label: "Newsletter",
      icon: Mail,
      count: counts?.newsletter_confirmed,
      match: (url) => url.startsWith("/admin/newsletter"),
    },
    {
      href: "/admin/testimonials?brand=gisela",
      label: "Testimonials",
      icon: MessageSquareQuote,
      count: counts?.testimonials_gisela,
      match: (url) => matchWithBrand(url, "/admin/testimonials", "gisela"),
    },
    {
      href: "/admin/therapists",
      label: "Her Profile",
      icon: UserCircle,
      match: (url) => url.startsWith("/admin/therapists"),
    },
  ]
}

function sanctuaryItems(counts?: AdminCounts): NavItem[] {
  return [
    {
      href: "/admin/analytics?brand=sanctuary",
      label: "Analytics",
      icon: BarChart2,
      match: (url) => matchWithBrand(url, "/admin/analytics", "sanctuary"),
    },
    {
      href: "/admin/enquiries?brand=sanctuary",
      label: "Enquiries",
      icon: Inbox,
      count: counts?.viewing_requests,
      match: (url) => matchWithBrand(url, "/admin/enquiries", "sanctuary"),
    },
    {
      href: "/admin/website?brand=sanctuary",
      label: "Website Pages",
      icon: ListTree,
      match: (url) =>
        matchWithBrand(url, "/admin/website", "sanctuary") ||
        url.startsWith("/admin/website/locations") ||
        (url.startsWith("/admin/website/pages/") &&
          !["ocd-therapy", "online-therapy", "gisela-"].some((k) => url.includes(k))),
    },
    {
      href: "/admin/testimonials?brand=sanctuary",
      label: "Testimonials",
      icon: MessageSquareQuote,
      count: counts?.testimonials_sanctuary,
      match: (url) => matchWithBrand(url, "/admin/testimonials", "sanctuary"),
    },
  ]
}

const TOOL_ITEMS: NavItem[] = [
  {
    href: "/admin/video-sessions/new",
    label: "Online sessions",
    icon: Video,
    match: (url) => url.startsWith("/admin/video-sessions"),
  },
  {
    href: "/admin/photos",
    label: "Photos",
    icon: Image,
    match: (url) => url.startsWith("/admin/photos"),
  },
  {
    href: "/admin/image-studio",
    label: "Image Studio",
    icon: Sparkles,
    match: (url) => url.startsWith("/admin/image-studio"),
  },
  {
    href: "/admin/videos",
    label: "Videos",
    icon: Film,
    match: (url) => url.startsWith("/admin/videos"),
  },
  {
    href: "/admin/competitors",
    label: "Competitor Watch",
    icon: Radar,
    match: (url) => url.startsWith("/admin/competitors"),
  },
  {
    href: "/admin/assistant",
    label: "Site Assistant",
    icon: MessageCircleQuestionMark,
    match: (url) => url.startsWith("/admin/assistant"),
  },
]

const SYSTEM_ITEMS: NavItem[] = [
  {
    href: "/admin/features",
    label: "Features",
    icon: ToggleLeft,
    match: (url) => url.startsWith("/admin/features"),
  },
  {
    href: "/admin/email",
    label: "Email",
    icon: Mail,
    match: (url) => url.startsWith("/admin/email"),
  },
  {
    href: "/admin/api",
    label: "API",
    icon: Key,
    match: (url) => url.startsWith("/admin/api"),
  },
  {
    href: "/admin/seo",
    label: "SEO",
    icon: Search,
    match: (url) => url.startsWith("/admin/seo"),
  },
  {
    href: "/admin/design-system",
    label: "Design system",
    icon: Palette,
    match: (url) => url.startsWith("/admin/design-system"),
  },
]

export function adminNavSections(counts?: AdminCounts): NavSection[] {
  return [
    { label: "Overview", items: overviewItems() },
    { label: "Gisela Ramirez", items: giselaItems(counts) },
    { label: "Sanctuary Therapy Centre", items: sanctuaryItems(counts) },
    { label: "Tools", items: TOOL_ITEMS },
    { label: "System", items: SYSTEM_ITEMS },
  ]
}
